// Status is now fully managed by the backend:
// - PENDING: Reminder not yet due
// - NOTIFIED: Notification sent, user has 10 minutes to complete
// - OVERDUE: 10+ minutes passed since notification, still not completed
// - COMPLETED: User manually marked as complete
// - CANCELLED: User cancelled the reminder
const deriveStatusFromReminder = (reminder: Reminder): ReminderStatus => {
  // Trust the backend status completely - no client-side derivation
  // The backend handles the full lifecycle: pending -> notified -> overdue
  // DO NOT read status from UI_METADATA - that was from old client-side logic

  // 1. Trust explicit backend status first (this is the primary source of truth)
  if (reminder.status && isValidStatus(reminder.status)) {
    return reminder.status;
  }

  // 2. Legacy isCompleted flag for backwards compatibility
  if (reminder.isCompleted === true) {
    return "completed";
  }

  // 3. Default to pending - let backend handle all status transitions
  // NOTE: We no longer read from UI_METADATA.status as that contained stale client-side values
  return "pending";
};

// Helper function to validate status - includes 'notified' status from backend
const isValidStatus = (status: string): status is ReminderStatus => {
  return ["pending", "notified", "completed", "cancelled", "overdue"].includes(status);
};
// Helper function to validate UUID v4 format
const isValidUUID = (uuid: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
};

// Helper function to generate a valid UUID v4 for UI metadata
const generateUIMetadataId = (): string => {
  // For browser environments that support crypto.randomUUID
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback UUID generation
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export const convertUIToApi = (
  uiReminder: Partial<ReminderUI>
): CreateReminderRequest | UpdateReminderRequest => {
  
  if (!uiReminder.title) throw new Error("Missing title for reminder.");
  // Use local timezone to avoid UTC date shift
  // Handle dueDate as either Date object or string
  const date = uiReminder.date || (uiReminder.dueDate ? (
    typeof uiReminder.dueDate === 'string'
      ? uiReminder.dueDate.split('T')[0] // Extract YYYY-MM-DD from ISO string
      : `${uiReminder.dueDate.getFullYear()}-${String(uiReminder.dueDate.getMonth() + 1).padStart(2, '0')}-${String(uiReminder.dueDate.getDate()).padStart(2, '0')}`
  ) : undefined);
  if (!date) throw new Error("Missing date for reminder.");

  // Convert reminderTime to HH:mm:ss format for the top-level field
  const reminderTimeStr = uiReminder.reminderTime ? (() => {
    // Handle both Date object and string
    if (typeof uiReminder.reminderTime === 'string') {
      // Already in HH:mm:ss format
      return uiReminder.reminderTime;
    }
    // Convert Date object to HH:mm:ss
    const d = uiReminder.reminderTime;
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  })() : '09:00:00'; // Default to 9 AM if not provided

  const uiMetadata: Record<string, unknown> = {
    ...(uiReminder.description && { description: uiReminder.description }),
    ...(uiReminder.type && { type: uiReminder.type }),
    // NOTE: Do NOT store status in UI_METADATA - status is now fully managed by the backend
    // The backend handles: pending -> notified -> overdue transitions
    ...(uiReminder.isCompleted !== undefined && { isCompleted: uiReminder.isCompleted }),
    ...(uiReminder.isRecurring !== undefined && { isRecurring: uiReminder.isRecurring }),
    ...(uiReminder.recurringPattern && { recurringPattern: uiReminder.recurringPattern }),
    ...(uiReminder.associatedClientId && {
      associatedClientId: uiReminder.associatedClientId,
    }),
    ...(uiReminder.associatedClientName && {
      associatedClientName: uiReminder.associatedClientName,
    }),
    ...(uiReminder.notes && { notes: uiReminder.notes }),
    ...(uiReminder.completedAt && {
      // Format as local ISO string WITHOUT UTC conversion
      completedAt: (() => {
        const d = uiReminder.completedAt;
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      })(),
    }),
  };

  const associations: ReminderAssociation[] = [];

  // Only add UI metadata if there's actual data and we have associations to preserve
  if (Object.keys(uiMetadata).length > 0) {
    associations.push({
      entityType: "UI_METADATA",
      entityId: generateUIMetadataId(), // Generate valid UUID
      metadata: uiMetadata,
    });
  }

  // Add non-UI metadata associations, but validate entityIds
  if (uiReminder.associations) {
    const validAssociations = uiReminder.associations
      .filter((assoc) => assoc.entityType !== "UI_METADATA")
      .filter((assoc) => {
        // Only include associations with valid entityIds
        return assoc.entityId && assoc.entityId.trim() !== "";
      })
      .map((assoc) => ({
        ...assoc,
        // Ensure entityId is a valid UUID, generate one if needed
        entityId: isValidUUID(assoc.entityId) ? assoc.entityId : generateUIMetadataId()
      }));
    
    associations.push(...validAssociations);
  }

  // Compute remindAt by combining date/dueDate with optional reminderTime
  let remindAt: string | undefined;
  try {
    // Create date in LOCAL timezone (not UTC) to avoid timezone shift
    let baseDate: Date;
    if (uiReminder.dueDate) {
      baseDate = new Date(uiReminder.dueDate);
    } else {
      // Parse date string in local timezone by NOT adding 'Z' suffix
      const [year, month, day] = date.split('-').map(Number);
      baseDate = new Date(year, month - 1, day, 0, 0, 0, 0);
    }

    if (uiReminder.reminderTime instanceof Date) {
      const hours = uiReminder.reminderTime.getHours();
      const minutes = uiReminder.reminderTime.getMinutes();
      baseDate.setHours(hours, minutes, 0, 0);
    }

    // Format as local datetime string WITHOUT UTC conversion
    // Format: YYYY-MM-DDTHH:MM:SS (without Z suffix to avoid UTC conversion)
    const year = baseDate.getFullYear();
    const month = String(baseDate.getMonth() + 1).padStart(2, '0');
    const day = String(baseDate.getDate()).padStart(2, '0');
    const hours = String(baseDate.getHours()).padStart(2, '0');
    const minutes = String(baseDate.getMinutes()).padStart(2, '0');
    const seconds = String(baseDate.getSeconds()).padStart(2, '0');

    remindAt = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
  } catch {
    // If date parsing fails for any reason, fall back to sending just the date string
    remindAt = date;
  }

  const result = {
    title: uiReminder.title,
    date,
    remindAt,
    text: uiReminder.text || uiReminder.description,
    // Include reminderTime directly on the reminder object
    reminderTime: reminderTimeStr,
    // Include priority directly on the reminder object for backend compatibility
    ...(uiReminder.priority ? { priority: uiReminder.priority } : {}),
    // Now we CAN include associations - backend DTO accepts them
    ...(associations.length > 0 ? { associations } : {}),
  };

  return result;
};

// Status types based on backend validation
// notified = notification sent, user has 10 min grace period before overdue
export type ReminderStatus = "pending" | "notified" | "completed" | "cancelled" | "overdue";

export interface ReminderAssociation {
  id?: string;
  reminderId?: string;
  entityType: string;
  entityId: string;
  notes?: string;
  metadata?: Record<string, unknown>;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
}

// API Response Interface (matches the backend)
export interface Reminder {
  id: string;
  userId: string;
  agencyId?: string; // Agency association for multi-agency support
  title: string;
  date: string;
  remindAt?: string; // ISO datetime string when the reminder should trigger
  text?: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  associations?: ReminderAssociation[];
  description?: string;
  type?: 'appointment' | 'follow-up' | 'task' | 'deadline' | 'birthday' | 'custom';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: Date | string; // Can be Date object or ISO string for timezone-safe transmission
  isCompleted?: boolean;
  isRecurring?: boolean;
  reminderTime?: Date | string; // Can be Date object or HH:mm:ss string from backend
  recurringPattern?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  associatedClientId?: string;
  associatedClientName?: string;
  notes?: string;
  completedAt?: Date;
  // Backend API fields for status
  status?: ReminderStatus;
  priorityStatus?: 'low' | 'medium' | 'high' | 'urgent';
}

// UI Interface (extends API with UI-specific fields)
export interface ReminderUI extends Reminder {
  // UI-specific fields that we'll manage locally
  description?: string;
  type: 'appointment' | 'follow-up' | 'task' | 'deadline' | 'birthday' | 'custom';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate: Date | string; // Can be Date object or ISO string for timezone-safe transmission
  isCompleted: boolean;
  isRecurring: boolean;
  recurringPattern?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  reminderTime?: Date | string; // Can be Date object in UI forms or HH:mm:ss string for backend
  associatedClientId?: string;
  associatedClientName?: string;
  notes?: string;
  completedAt?: Date;
  // Enhanced status management
  status: ReminderStatus;
}

export interface CreateReminderRequest {
  title: string;
  date: string;
  text?: string;
  reminderTime?: string; // HH:mm:ss format
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  associations?: Omit<ReminderAssociation, 'id' | 'reminderId' | 'createdAt' | 'updatedAt' | 'deletedAt'>[];
}

export interface UpdateReminderRequest {
  title?: string;
  date?: string;
  text?: string;
  reminderTime?: string; // HH:mm:ss format
  notes?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>; // adjust typing if needed
  associations?: Omit<
    ReminderAssociation,
    'id' | 'reminderId' | 'createdAt' | 'updatedAt' | 'deletedAt'
  >[];
}

// Status update request type
export interface UpdateReminderStatusRequest {
  remStat?: ReminderStatus;
  remPriorStat?: 'urgent' | 'high' | 'medium' | 'low';
}

export interface RemindersResponse {
  meta: {
    page: number;
    nextPage: number | null;
    prevPage: number | null;
    offset: number;
    totalItems: number;
    totalPages: number;
    itemCount: number;
  };
  items: Reminder[];
}

export interface ReminderFilters {
  entityType?: string;
  status?: 'all' | 'pending' | 'notified' | 'completed' | 'overdue' | 'cancelled';
  dateRange?: 'today' | 'week' | 'month' | 'all';
  type?: string;
  priority?: string;
}

// Helper functions to convert between API and UI formats
export const convertApiToUI = (apiReminder: Reminder): ReminderUI => {
  if (!apiReminder?.date) {
    throw new Error("Reminder is missing 'date' field.");
  }

  // Parse date in local timezone to avoid UTC conversion issues
  let dueDate: Date;
  if (/^\d{4}-\d{2}-\d{2}$/.test(apiReminder.date)) {
    const [year, month, day] = apiReminder.date.split('-').map(Number);
    dueDate = new Date(year, month - 1, day, 12, 0, 0, 0); // Set to noon local time
  } else {
    dueDate = new Date(apiReminder.date);
  }

  const uiMetadata = apiReminder.associations?.find(
    (assoc) => assoc.entityType === "UI_METADATA"
  )?.metadata || {};

  // Find lead association from the associations array (case-insensitive check)
  const leadAssociation = apiReminder.associations?.find(
    (assoc) => assoc.entityType?.toLowerCase() === "lead"
  );

  // Derive status from multiple sources
  const status = deriveStatusFromReminder(apiReminder);
  const isCompleted = status === "completed" || !!apiReminder.isCompleted || !!uiMetadata.isCompleted;

  return {
    ...apiReminder,
    description: apiReminder.text || (uiMetadata.description as string) || "",
    type: (uiMetadata.type as ReminderUI["type"]) || "task",
    priority: (apiReminder.priority || apiReminder.priorityStatus || uiMetadata.priority as ReminderUI["priority"]) || "medium",
    dueDate,
    isCompleted,
    status,
    isRecurring: !!uiMetadata.isRecurring,
    recurringPattern: uiMetadata.recurringPattern as ReminderUI["recurringPattern"],
    reminderTime: (() => {
      // First check if reminderTime exists as a top-level field from the backend
      const timeStr = apiReminder.reminderTime || uiMetadata.reminderTime as string;

      // Parse time string (can be HH:mm:ss or full ISO datetime for backwards compatibility)
      if (timeStr && typeof timeStr === 'string') {
        // Check if it's a time-only string (HH:mm:ss or HH:mm)
        if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(timeStr)) {
          const [hours, minutes, seconds] = timeStr.split(':').map(Number);
          // Create a Date object with today's date but the specified time
          const timeDate = new Date();
          timeDate.setHours(hours, minutes, seconds || 0, 0);
          return timeDate;
        }
        // Backwards compatibility: parse full ISO datetime
        if (timeStr.includes('T')) {
          const [datePart, timePart] = timeStr.split('T');
          const [year, month, day] = datePart.split('-').map(Number);
          const [hours, minutes, seconds] = timePart.replace('Z', '').split(':').map(Number);
          const result = new Date(year, month - 1, day, hours, minutes, seconds || 0);
          return result;
        }
      }
      // Default to 9 AM if not provided
      const defaultTime = new Date();
      defaultTime.setHours(9, 0, 0, 0);
      return defaultTime;
    })(),
    // Get lead ID from lead association first, then fall back to uiMetadata
    associatedClientId: leadAssociation?.entityId || (uiMetadata.associatedClientId as string),
    // Get client name from multiple sources: uiMetadata, lead association notes, or lead association metadata
    associatedClientName: (uiMetadata.associatedClientName as string) ||
      (leadAssociation?.notes?.startsWith("Associated with ")
        ? leadAssociation.notes.replace("Associated with ", "")
        : undefined) ||
      (leadAssociation?.metadata?.associatedClientName as string) ||
      undefined,
    notes: uiMetadata.notes as string,
    completedAt: uiMetadata.completedAt
      ? (() => {
          const timeStr = uiMetadata.completedAt as string;
          // Parse ISO datetime in local timezone (not UTC)
          if (typeof timeStr === 'string' && timeStr.includes('T')) {
            const [datePart, timePart] = timeStr.split('T');
            const [year, month, day] = datePart.split('-').map(Number);
            const [hours, minutes, seconds] = timePart.replace('Z', '').split(':').map(Number);
            return new Date(year, month - 1, day, hours, minutes, seconds || 0);
          }
          return new Date(timeStr);
        })()
      : undefined,
  };
};
